import React, { useState } from "react";
import { Address } from "../types";

interface SelectAddressStepProps {
  addresses: Address[];
  onSelect: (address: Address) => void;
  onBack: () => void;
  loading: boolean;
}

const SelectAddressStep: React.FC<SelectAddressStepProps> = ({
  addresses,
  onSelect,
  onBack,
  loading,
}) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleSelect = (address: Address) => {
    setSelectedId(address.id);
    onSelect(address);
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 sm:p-8 border-2 border-crisp-orange">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3 mb-2">
          <svg
            className="w-8 h-8 text-crisp-orange flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
            />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
            />
          </svg>
          <h2 className="text-2xl sm:text-3xl font-bold text-crisp-dark-blue">
            Select Your Address
          </h2>
        </div>
        <p className="text-gray-600 text-sm sm:text-base ml-11">
          We found {addresses.length} matching addresses. Please select the
          correct one:
        </p>
      </div>

      {/* Address Options */}
      <div className="space-y-3 mb-6 max-h-[60vh] overflow-y-auto">
        {addresses.map((addr, index) => (
          <button
            key={addr.id}
            onClick={() => handleSelect(addr)}
            disabled={loading}
            className={`
              w-full text-left p-4 sm:p-5 rounded-xl border-2 transition-all duration-200
              ${
                selectedId === addr.id && loading
                  ? "border-crisp-orange bg-orange-50 ring-2 ring-crisp-orange ring-opacity-50"
                  : "border-gray-200 hover:border-crisp-orange hover:bg-orange-50 hover:shadow-md"
              }
              disabled:opacity-50 disabled:cursor-not-allowed
              focus:outline-none focus:ring-2 focus:ring-crisp-orange focus:ring-opacity-50
              active:scale-[0.99]
              group
            `}
          >
            <div className="flex items-start gap-3">
              {/* Number Badge */}
              <div
                className={`
                flex-shrink-0 w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${
                  selectedId === addr.id && loading
                    ? "bg-crisp-orange text-white"
                    : "bg-gray-100 text-gray-600 group-hover:bg-crisp-orange group-hover:text-white"
                }
                transition-colors duration-200
              `}
              >
                {index + 1}
              </div>

              {/* Address Content */}
              <div className="flex-1 min-w-0">
                {/* Main Address */}
                <div className="font-semibold text-crisp-dark-blue text-base sm:text-lg mb-2 break-words">
                  {addr.formatted_address}
                </div>

                {/* Address Details */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-gray-600">
                  {addr.street_number && addr.street_name && (
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                        />
                      </svg>
                      <span className="truncate">
                        {addr.street_number} {addr.street_name}
                      </span>
                    </div>
                  )}
                  {addr.suburb && (
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                        />
                      </svg>
                      <span className="truncate">{addr.suburb}</span>
                    </div>
                  )}
                  {addr.state && (
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7"
                        />
                      </svg>
                      <span className="truncate">{addr.state}</span>
                    </div>
                  )}
                  {addr.postcode && (
                    <div className="flex items-center gap-1.5">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                      <span>{addr.postcode}</span>
                    </div>
                  )}
                  {addr.unit && (
                    <div className="flex items-center gap-1.5 col-span-full">
                      <svg
                        className="w-4 h-4 text-gray-400 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"
                        />
                      </svg>
                      <span className="text-crisp-orange font-medium">
                        Unit {addr.unit}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Selection Indicator */}
              {selectedId === addr.id && loading && (
                <div className="flex-shrink-0">
                  <svg
                    className="animate-spin h-5 w-5 text-crisp-orange"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                </div>
              )}
            </div>
          </button>
        ))}
      </div>

      {/* Back Button */}
      <button
        onClick={onBack}
        disabled={loading}
        className="flex items-center gap-2 text-crisp-orange hover:text-orange-600 font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
        Back to address search
      </button>
    </div>
  );
};

export default SelectAddressStep;
